/**
 * Hook para gerenciamento de Clientes CRM
 * 
 * Fornece queries e mutations para CRUD de clientes
 * Clientes são criados automaticamente quando deals são ganhos
 * 
 * NOTA: Usar casting 'as any' até types.ts ser regenerado
 * 
 * CHANGELOG:
 *   RELEASE: 2026-09-04 v1.1.0 - Criar/atualizar cliente passa a invalidar
 *            'workflow-guard-check' e 'deal-workflow', liberando as abas
 *            Propostas/Contratos/Financeiro sem F5 quando a exigência do
 *            processo era "cliente cadastrado".
 *   RELEASE: v1.0.0 - Versão inicial.
 *
 * @version 1.1.0
 * @author Sistema
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CrmCustomers');

export type CustomerType = 'pf' | 'pj';
export type CustomerLifecycleStage = 'active' | 'inactive' | 'churned';

export interface CrmCustomer {
  id: string;
  tenant_id: string;
  main_contact_id: string;
  origin_deal_id: string | null;
  customer_type: CustomerType;
  nome: string | null;
  cpf: string | null;
  data_nascimento: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  seller_id: string | null;
  lifecycle_stage: CustomerLifecycleStage;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  main_contact?: { 
    id: string; 
    name: string | null; 
    phone: string | null; 
    email: string | null; 
    avatar_url: string | null;
    // Campos de redes sociais v1.0.1
    channel_ids?: {
      instagram_username?: string;
      instagram_igsid?: string;
      facebook_psid?: string;
      webchat_visitor_id?: string;
    } | null;
    metadata?: Record<string, unknown> | null;
  } | null;
  seller?: { id: string; name: string; email: string | null; phone: string | null; avatar_url: string | null; } | null;
  origin_deal?: { id: string; title: string; value: number; } | null;
}

export interface UpdateCustomerInput {
  id: string;
  customer_type?: CustomerType;
  nome?: string | null;
  cpf?: string | null;
  data_nascimento?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  seller_id?: string | null;
  lifecycle_stage?: CustomerLifecycleStage;
  notes?: string | null;
  metadata?: Record<string, unknown>;
}

/**
 * Interface para criação manual de cliente
 * main_contact_id é obrigatório
 */
export interface CreateCustomerInput {
  main_contact_id: string;
  customer_type: CustomerType;
  nome?: string | null;
  cpf?: string | null;
  data_nascimento?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  seller_id?: string | null;
  lifecycle_stage?: CustomerLifecycleStage;
  notes?: string | null;
}

export interface CustomerFilters {
  customer_type?: CustomerType | null;
  lifecycle_stage?: CustomerLifecycleStage | null;
  seller_id?: string | null;
  search?: string;
}

/**
 * Opções do hook useCrmCustomers
 *
 * IMPORTANTE: Por padrão a query de listagem (`SELECT * FROM crm_customers ...`)
 * está DESABILITADA para evitar requisições desnecessárias quando o componente
 * só precisa das mutations (update/delete/changeLifecycleStage).
 *
 * Para componentes que realmente precisam carregar a lista (raros — a maioria
 * deve usar `useCrmCustomersPaginated` via RPC server-side), passe
 * `{ enabled: true }`.
 */
export interface UseCrmCustomersOptions {
  /** Habilita a query de listagem direta na tabela. Default: false */
  enabled?: boolean;
}

export function useCrmCustomers(
  filters?: CustomerFilters,
  options?: UseCrmCustomersOptions,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Listagem opt-in: só executa se explicitamente habilitada
  const listEnabled = !!currentTenant?.id && (options?.enabled ?? false);

  const customersQuery = useQuery({
    queryKey: ['crm-customers', currentTenant?.id, filters],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      // DIAGNÓSTICO: Select simplificado para identificar problema nos embeds
      // Etapa 1: Buscar apenas dados básicos do cliente (sem embeds)
      // Se funcionar, problema está em algum embed (main_contact, seller, origin_deal)
      let query = (supabase.from('crm_customers' as any) as any)
        .select(`
          *,
          main_contact:contacts(id, name, phone, email, avatar_url),
          seller:crm_sellers(id, name, email, phone, avatar_url)
        `)
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: false });
      
      // NOTA: origin_deal removido temporariamente para isolar problema
      // Se listar clientes com sucesso, reintroduzir o embed de crm_deals
      if (filters?.customer_type) query = query.eq('customer_type', filters.customer_type);
      if (filters?.lifecycle_stage) query = query.eq('lifecycle_stage', filters.lifecycle_stage);
      if (filters?.seller_id) query = query.eq('seller_id', filters.seller_id);
      if (filters?.search) query = query.or(`nome.ilike.%${filters.search}%,razao_social.ilike.%${filters.search}%,cpf.ilike.%${filters.search}%,cnpj.ilike.%${filters.search}%`);
      const { data, error } = await query;
      if (error) { logger.error('Erro ao buscar clientes:', error.message); throw error; }
      return data as CrmCustomer[];
    },
    enabled: listEnabled,
  });

  /**
   * Mutation para criar cliente via RPC atômica
   * Utiliza arquitetura de segurança Tenant v2.0
   * Todas validações são feitas no banco (atomicidade garantida)
   */
  const createCustomer = useMutation({
    mutationFn: async (input: CreateCustomerInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não selecionado');
      if (!input.main_contact_id) throw new Error('Contato principal é obrigatório');

      // Chama RPC atômica com tenant_hint para validação segura
      // NOTA: Usar casting até types.ts ser regenerado com a nova função
      const { data, error } = await (supabase.rpc as any)('create_crm_customer', {
        p_main_contact_id: input.main_contact_id,
        p_customer_type: input.customer_type,
        p_nome: input.nome || null,
        p_cpf: input.cpf || null,
        p_data_nascimento: input.data_nascimento || null,
        p_razao_social: input.razao_social || null,
        p_nome_fantasia: input.nome_fantasia || null,
        p_cnpj: input.cnpj || null,
        p_cep: input.cep || null,
        p_logradouro: input.logradouro || null,
        p_numero: input.numero || null,
        p_complemento: input.complemento || null,
        p_bairro: input.bairro || null,
        p_cidade: input.cidade || null,
        p_estado: input.estado || null,
        p_seller_id: input.seller_id || null,
        p_lifecycle_stage: input.lifecycle_stage || 'active',
        p_notes: input.notes || null,
        p_tenant_hint: currentTenant.id
      });

      if (error) {
        logger.error('Erro ao criar cliente via RPC:', error.message);
        throw new Error(error.message);
      }

      // Retorna objeto mínimo com ID para invalidação de cache
      return { id: data } as CrmCustomer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
      // v1.1.0: o cadastro do cliente não grava no negócio, então nenhum gatilho de
      // workflow dispara. Invalidamos a trava e a trilha do processo para que a
      // exigência "cliente cadastrado" seja quitada na próxima leitura, sem F5.
      queryClient.invalidateQueries({ queryKey: ['workflow-guard-check'] });
      queryClient.invalidateQueries({ queryKey: ['deal-workflow'] });
      toast.success('Cliente criado com sucesso!');
    },

    onError: (error: Error) => {
      toast.error(`Erro ao criar cliente: ${error.message}`);
    },
  });

  const updateCustomer = useMutation({
    mutationFn: async (input: UpdateCustomerInput) => {
      const { id, ...updateData } = input;
      const cleanData = Object.fromEntries(Object.entries(updateData).filter(([_, v]) => v !== undefined));
      const { data, error } = await (supabase.from('crm_customers' as any) as any).update(cleanData).eq('id', id).select().single();
      if (error) { logger.error('Erro ao atualizar cliente:', error.message); throw error; }
      return data as CrmCustomer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
      queryClient.invalidateQueries({ queryKey: ['crm-customer', data.id] });
      // v1.1.0: vínculo/atualização de cliente também pode satisfazer exigência do processo
      queryClient.invalidateQueries({ queryKey: ['workflow-guard-check'] });
      queryClient.invalidateQueries({ queryKey: ['deal-workflow'] });
      toast.success('Cliente atualizado com sucesso!');
    },

    onError: (error: Error) => { toast.error(`Erro ao atualizar cliente: ${error.message}`); },
  });

  const deleteCustomer = useMutation({
    mutationFn: async (customerId: string) => {
      const { error } = await (supabase.from('crm_customers' as any) as any).update({ lifecycle_stage: 'churned' }).eq('id', customerId);
      if (error) { logger.error('Erro ao excluir cliente:', error.message); throw error; }
      return customerId;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['crm-customers'] }); toast.success('Cliente marcado como perdido.'); },
    onError: (error: Error) => { toast.error(`Erro ao excluir cliente: ${error.message}`); },
  });

  const changeLifecycleStage = useMutation({
    mutationFn: async ({ customerId, stage }: { customerId: string; stage: CustomerLifecycleStage }) => {
      const { data, error } = await (supabase.from('crm_customers' as any) as any).update({ lifecycle_stage: stage }).eq('id', customerId).select().single();
      if (error) { logger.error('Erro ao alterar status:', error.message); throw error; }
      return data as CrmCustomer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
      const labels: Record<CustomerLifecycleStage, string> = { active: 'Ativo', inactive: 'Inativo', churned: 'Perdido' };
      toast.success(`Cliente marcado como ${labels[data.lifecycle_stage]}`);
    },
    onError: (error: Error) => { toast.error(`Erro ao alterar status: ${error.message}`); },
  });

  return {
    customers: customersQuery.data || [],
    isLoading: customersQuery.isLoading,
    error: customersQuery.error,
    refetch: customersQuery.refetch,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    changeLifecycleStage,
    isCreating: createCustomer.isPending,
    isUpdating: updateCustomer.isPending,
    isDeleting: deleteCustomer.isPending,
  };
}

/**
 * Interface do retorno da RPC get_customer_by_id
 * Estrutura flat retornada pelo banco
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-12
 */
interface CustomerByIdRpcResult {
  id: string;
  customer_type: string;
  nome: string | null;
  cpf: string | null;
  data_nascimento: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  lifecycle_stage: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  origin_deal_id: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  // Contato principal
  main_contact_id: string;
  main_contact_name: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  main_contact_avatar: string | null;
  // Campos de redes sociais do contato v1.0.1
  main_contact_channel_ids: {
    instagram_username?: string;
    instagram_igsid?: string;
    facebook_psid?: string;
    webchat_visitor_id?: string;
  } | null;
  main_contact_metadata: Record<string, unknown> | null;
  // Vendedor (avatar já com fallback aplicado na RPC)
  seller_id: string | null;
  seller_name: string | null;
  seller_email: string | null;
  seller_phone: string | null;
  seller_avatar: string | null;
}

/**
 * Transforma resultado flat da RPC em estrutura aninhada CrmCustomer
 * Mantém compatibilidade com componentes existentes
 * 
 * @param row - Resultado da RPC get_customer_by_id
 * @returns CrmCustomer com estrutura aninhada
 */
function transformCustomerRpcResult(row: CustomerByIdRpcResult): CrmCustomer {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    main_contact_id: row.main_contact_id,
    origin_deal_id: row.origin_deal_id,
    customer_type: row.customer_type as CustomerType,
    nome: row.nome,
    cpf: row.cpf,
    data_nascimento: row.data_nascimento,
    razao_social: row.razao_social,
    nome_fantasia: row.nome_fantasia,
    cnpj: row.cnpj,
    cep: row.cep,
    logradouro: row.logradouro,
    numero: row.numero,
    complemento: row.complemento,
    bairro: row.bairro,
    cidade: row.cidade,
    estado: row.estado,
    seller_id: row.seller_id,
    lifecycle_stage: row.lifecycle_stage as CustomerLifecycleStage,
    notes: row.notes,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    // Estruturas aninhadas para compatibilidade
    main_contact: row.main_contact_id ? {
      id: row.main_contact_id,
      name: row.main_contact_name,
      phone: row.main_contact_phone,
      email: row.main_contact_email,
      avatar_url: row.main_contact_avatar,
      // Redes sociais do contato v1.0.1
      channel_ids: row.main_contact_channel_ids,
      metadata: row.main_contact_metadata,
    } : null,
    // Vendedor com avatar já com fallback (vem da RPC)
    seller: row.seller_id ? {
      id: row.seller_id,
      name: row.seller_name || '',
      email: row.seller_email,
      phone: row.seller_phone,
      avatar_url: row.seller_avatar, // Já com COALESCE aplicado na RPC
    } : null,
    origin_deal: null, // RPC não retorna origin_deal completo, apenas ID
  };
}

/**
 * Hook para buscar cliente por ID via RPC
 * Usa RPC get_customer_by_id que implementa fallback de avatar do vendedor
 * 
 * @version 2.0.0
 * RELEASE: 2026-01-12 - Migrado para RPC com fallback de avatar
 */
export function useCrmCustomerById(customerId: string | null) {
  const { currentTenant } = useAuth();
  
  return useQuery({
    queryKey: ['crm-customer', customerId],
    queryFn: async () => {
      if (!customerId || !currentTenant?.id) return null;
      
      logger.debug('Buscando cliente via RPC:', { customerId });

      // Chamada RPC com validação de tenant
      const { data, error } = await (supabase.rpc as any)('get_customer_by_id', {
        p_customer_id: customerId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar cliente via RPC:', error.message);
        throw error;
      }

      // RPC retorna array, pegar primeiro (e único) resultado
      if (!data || data.length === 0) {
        logger.warn('Cliente não encontrado:', customerId);
        return null;
      }

      // Transformar resultado flat em estrutura aninhada
      return transformCustomerRpcResult(data[0] as CustomerByIdRpcResult);
    },
    enabled: !!customerId && !!currentTenant?.id,
  });
}
